import { invoke } from "@tauri-apps/api/core";

type DesktopMarker = { engine?: string; platform?: string };
type ConsentInvoker = (command: string, args: { operation: string }) => Promise<unknown>;
const desktopMarker = () => (globalThis as { __LINK_DESKTOP__?: DesktopMarker }).__LINK_DESKTOP__;

/** Reconcile saved consent BEFORE capture without interrupting voice calls.
 * Only first-run setup/settings prompt for microphone consent. */
async function nativeMediaPermission(
  device: "microphone" | "camera", operation: string, desktop: DesktopMarker | undefined, call: ConsentInvoker,
): Promise<void> {
  if (desktop?.engine !== "tauri" || desktop.platform !== "windows") return;
  let status: unknown;
  try { status = await call("media_permissions", { operation }); }
  catch (error) {
    const detail = error instanceof Error ? error.message : String(error);
    if (/permission was not granted|request was cancelled/i.test(detail)) {
      throw new DOMException(`Link ${device} permission was not granted.`, "NotAllowedError");
    }
    throw new Error(`Link couldn't open ${device} permission. Restart the latest desktop app and try again.`);
  }
  if (!status || typeof status !== "object" || (status as Record<string, unknown>)[device] !== true) {
    throw new DOMException(`Link ${device} permission was not granted.`, "NotAllowedError");
  }
}

export function requestNativeMediaPermission(
  device: "microphone" | "camera", desktop = desktopMarker(), call: ConsentInvoker = invoke,
): Promise<void> {
  return nativeMediaPermission(device, device === "microphone" ? "ensure_microphone" : "request_camera", desktop, call);
}

/** Explicit settings action only. This may show the native microphone dialog. */
export function requestMicrophoneSetup(desktop = desktopMarker(), call: ConsentInvoker = invoke): Promise<void> {
  return nativeMediaPermission("microphone", "request_microphone", desktop, call);
}

export async function requestMediaStream(
  devices: Pick<MediaDevices, "getUserMedia">, withVideo: boolean,
  desktop = desktopMarker(), call: ConsentInvoker = invoke,
): Promise<MediaStream> {
  await requestNativeMediaPermission("microphone", desktop, call);
  if (withVideo) await requestNativeMediaPermission("camera", desktop, call);
  return devices.getUserMedia({ audio: true, video: withVideo });
}

/** UI guidance only. This marker never grants access or changes encryption. */
export function mediaPermissionDeniedMessage(withVideo: boolean, desktop?: unknown): string {
  const devices = withVideo ? "microphone and camera" : "microphone";
  const prefix = `Link needs ${devices} permission to connect this call.`;
  const shell = desktop as { engine?: string; platform?: string } | undefined;
  if (shell?.engine === "tauri" && shell.platform === "windows") {
    return `${prefix} Open Settings → Voice & video to enable microphone access or reset a blocked choice. If Windows is blocking access, check Windows Settings → Privacy & security → ${withVideo ? "Microphone and Camera" : "Microphone"} → Let desktop apps access your ${devices}.`;
  }
  if (shell?.engine === "tauri" && shell.platform === "macos") {
    return `${prefix} Allow Link in System Settings → Privacy & Security → ${withVideo ? "Microphone and Camera" : "Microphone"}, then try again.`;
  }
  return `${prefix} Allow it in the browser's site settings and try again.`;
}

/** User-initiated, local-only capture check. No playback, upload, or recording. */
export async function checkMicrophoneAccess(
  devices: Pick<MediaDevices, "getUserMedia">, desktop = desktopMarker(), call: ConsentInvoker = invoke,
): Promise<void> {
  await requestMicrophoneSetup(desktop, call);
  const stream = await requestMediaStream(devices, false, desktop, call);
  try {
    if (!stream.getAudioTracks().some((track) => track.readyState === "live")) {
      throw new Error("No active microphone was found. Connect a microphone and try again.");
    }
  } finally {
    stream.getTracks().forEach((track) => track.stop());
  }
}
